import { styled } from '@mui/material/styles';
import { Box, IconButton, Table, TableCell, TableHead, TableRow, Tabs, Typography } from '@mui/material';

// 피그마(CRM_센터관리_포인트관리, 6368:9015) 색상 토큰
const COLORS = {
  teal: '#4ab3bc',
  tealBg: '#f3f9fa',
  coral: '#ee735a',
  coralBg: '#ffeceb',
  border: '#d9d9d9',
  headerBg: '#f2f2f2',
  text: '#262626',
  grayText: '#595959',
  grayLine: '#a6a6a6',
};

// ============================================
// Summary Cards (총 적립 / 총 차감 포인트)
// ============================================
// 좁은 화면에서 카드가 찌그러져 금액이 잘리지 않도록 기본 폭(480px)을 확보하고,
// 공간이 부족하면 SettingsRow의 flexWrap으로 줄바꿈해 전체 폭을 쓴다.
export const SummaryCardsRow = styled(Box)({
  flex: '1 1 480px',
  minWidth: 0,
  display: 'flex',
  gap: '24px',
});

export const SummaryCard = styled(Box, {
  shouldForwardProp: prop => prop !== 'cardVariant',
})(({ cardVariant }) => ({
  flex: 1,
  minWidth: 0,
  height: '100px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '12px',
  padding: '0 clamp(16px, 1.7vw, 32px)',
  borderRadius: '16px',
  backgroundColor: cardVariant === 'earn' ? COLORS.tealBg : COLORS.coralBg,
}));

// 폰트는 피그마 28px 기준, 화면이 좁아지면 잘리는 대신 글자를 줄인다 (1.46vw ≈ 28px/1920)
export const SummaryCardLabel = styled(Typography, {
  shouldForwardProp: prop => prop !== 'cardVariant',
})(({ cardVariant }) => ({
  fontSize: 'clamp(18px, 1.46vw, 28px)',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  color: cardVariant === 'earn' ? COLORS.teal : COLORS.coral,
}));

export const SummaryCardValue = styled(Typography, {
  shouldForwardProp: prop => prop !== 'cardVariant',
})(({ cardVariant }) => ({
  fontSize: 'clamp(18px, 1.46vw, 28px)',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  color: cardVariant === 'earn' ? COLORS.teal : COLORS.coral,
}));

// ============================================
// 내역 섹션 (툴바 + 테이블 + 페이지네이션)
// ============================================
// 호출부(센터관리/회원상세)의 부모 컨테이너 gap과 무관하게 섹션 내부 간격을 일정하게 유지
export const HistorySectionContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  width: '100%',
});

// 내역 툴바 (좌: 밑줄 탭 / 우: 검색·기간·엑셀·페이지 크기)
export const HistoryToolbarRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: '12px',
});

export const HistoryStyledTabs = styled(Tabs)({
  minHeight: '44px',
  '& .MuiTab-root': {
    minHeight: '44px',
    minWidth: 'auto',
    padding: '0 16px',
    fontSize: '20px',
    fontWeight: 600,
    color: COLORS.grayText,
    textTransform: 'none',
    // 비선택 탭도 회색 밑줄을 유지 (선택 탭은 indicator가 teal로 덮음)
    borderBottom: `2px solid ${COLORS.grayLine}`,
  },
  '& .MuiTab-root.Mui-selected': {
    color: COLORS.teal,
  },
  '& .MuiTabs-indicator': {
    height: '2px',
    backgroundColor: COLORS.teal,
  },
});

export const ToolbarControls = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '8px',
});

// 피그마 268px 기준, 좁은 화면에서는 비례 축소 (14vw ≈ 268px/1920)
export const SearchInputWrapper = styled(Box)({
  width: 'clamp(180px, 14vw, 268px)',
});

// 날짜 인풋 폭 고정 — FormInputDate 기본 폭(336px)이 피그마(~150px)보다 넓어 래퍼로 제한
export const DateInputWrapper = styled(Box)({
  width: '160px',
  '& .MuiTextField-root': {
    width: '100%',
  },
});

export const SearchIconButton = styled(IconButton)({
  padding: '4px',
  color: COLORS.grayLine,
});

export const DateSeparator = styled(Typography)({
  fontSize: '16px',
  color: '#737373',
});

// ============================================
// Table
// ============================================
export const StyledTableContainer = styled(Box)({
  width: '100%',
  overflowX: 'auto',
  border: `1px solid ${COLORS.border}`,
  borderRadius: '8px 8px 0 0',
});

// 고정 컬럼 합(160+160+320+120+200+93+120=1173) + '내용' flex 컬럼 최소폭 확보.
// 이보다 좁아지면 컬럼이 찌부되는 대신 컨테이너가 가로 스크롤된다.
export const StyledTable = styled(Table)({
  minWidth: 1280,
});

export const StyledTableHead = styled(TableHead)({
  background: COLORS.headerBg,
});

export const HeaderCell = styled(TableCell)({
  padding: '12px 16px',
  fontSize: '14px',
  fontWeight: 600,
  color: COLORS.text,
  textAlign: 'center',
  whiteSpace: 'nowrap',
  borderBottom: `1px solid ${COLORS.border}`,
  borderRight: `1px solid ${COLORS.border}`,
  '&:last-child': {
    borderRight: 'none',
  },
});

export const BodyRow = styled(TableRow)({
  '&:last-child td, &:last-child th': { borderBottom: 0 },
  '&:hover': { background: '#fafafa' },
});

export const BodyCell = styled(TableCell)({
  padding: '12px 16px',
  fontSize: '14px',
  fontWeight: 500,
  color: COLORS.text,
  textAlign: 'center',
  borderBottom: `1px solid ${COLORS.border}`,
  borderRight: `1px solid ${COLORS.border}`,
  '&:last-child': {
    borderRight: 'none',
  },
});

export const EmptyCell = styled(TableCell)({
  padding: '60px 0',
  textAlign: 'center',
  color: '#9e9e9e',
  fontSize: '14px',
});

// 적용 일시 셀의 날짜/시간 2줄 표기
export const DateCellText = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: COLORS.text,
  lineHeight: 1.4,
});

// 적립/차감 상태 칩 (채운 사각 칩)
export const StatusChip = styled(Box, {
  shouldForwardProp: prop => prop !== 'chipType' && prop !== 'muted',
})(({ chipType, muted }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '69px',
  padding: '6px 12px',
  borderRadius: '2px',
  fontSize: '14px',
  fontWeight: 600,
  color: '#fff',
  // 취소된 내역(muted)은 적립/차감 대신 '취소' 라벨 — 회색 칩
  backgroundColor: muted ? '#8A8F98' : chipType === 'EARN' ? COLORS.teal : COLORS.coral,
}));

// 포인트 값(+1,200 P / -1,200 P)
export const PointValueText = styled(Typography, {
  shouldForwardProp: prop => prop !== 'chipType',
})(({ chipType }) => ({
  fontSize: '18px',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  color: chipType === 'EARN' ? COLORS.teal : COLORS.coral,
}));

// 증감액(PointValueText) 아래에 결과 잔액을 작은 회색 보조 텍스트로 표기 (토스형 2줄)
export const BalanceText = styled(Typography)({
  display: 'block',
  marginTop: '2px',
  fontSize: '12px',
  fontWeight: 500,
  color: '#9CA3AF',
  whiteSpace: 'nowrap',
});

// 클릭 가능한 회원명 (피그마: 본문색 + 밑줄)
export const MemberNameLink = styled('span')({
  cursor: 'pointer',
  textDecoration: 'underline',
  textUnderlineOffset: '2px',
});

// 내역 행의 변경 열 — '수정' '취소' 작은 아웃라인 버튼 두 개
export const RowActions = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
});

export const RowActionButton = styled('button', {
  shouldForwardProp: prop => prop !== 'danger',
})(({ danger }) => ({
  height: '30px',
  padding: '0 12px',
  borderRadius: '6px',
  border: `1px solid ${danger ? '#F1B5AE' : '#C9CED6'}`,
  background: '#fff',
  fontSize: '13px',
  fontWeight: 600,
  fontFamily: 'inherit',
  color: danger ? '#E0483A' : '#3D3D3D',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  '&:hover': { background: danger ? '#FFF6F5' : '#F5F6F8' },
  '&:disabled': { opacity: 0.4, cursor: 'default', background: '#fff' },
}));

// 취소 확인 모달의 요약 표 (적립/남은/회수 포인트)
export const CancelDetailBox = styled(Box)({
  margin: '4px 0 16px',
  border: '1px solid #E7EAEF',
  borderRadius: '8px',
  background: '#F7F8FA',
  padding: '10px 14px',
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  textAlign: 'left',
});

export const CancelDetailRow = styled(Box, {
  shouldForwardProp: prop => prop !== 'highlight',
})(({ highlight }) => {
  // highlight: 'minus'=회수(빨강) / 'plus'=복구(청록)
  const accent = highlight === 'plus' ? '#2E9AA4' : '#E0483A';
  return {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '13px',
    color: highlight ? accent : '#4A4F57',
    '& b': { fontWeight: 700, color: highlight ? accent : '#111' },
    ...(highlight ? { borderTop: '1px dashed #DDE1E8', paddingTop: '6px', marginTop: '2px' } : {}),
  };
});

// 적립 취소 안내 — 카드 밑, 모달 제목과 같은 가운데 정렬. 금액만 굵게
export const CancelWarning = styled(Box)({
  margin: '-4px 0 20px',
  textAlign: 'center',
  color: '#E0483A',
  fontSize: '13px',
  fontWeight: 500,
  lineHeight: 1.6,
  '& b': { fontWeight: 700 },
});

export const CancelDetailNote = styled(Box)({
  fontSize: '11px',
  color: '#8A8F98',
  marginTop: '2px',
});
